import { useEffect, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Product } from '../types';
import { ProductCard } from '../components/catalog/ProductCard';
import { ArrowRight, Sparkles, ShieldCheck, Flame, Layers } from 'lucide-react';

export function HomePage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { settings, categories } = useStore();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/products?limit=6&sort=featured')
      .then((res) => res.json())
      .then((data: any) => {
        setFeaturedProducts(data.products || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-20 sm:space-y-28">
      {/* 1. Hero Section */}
      <section className="relative min-h-[80vh] flex items-center bg-background-alt border-b border-border-subtle overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Hero Editorial Text */}
            <div className="lg:col-span-7 space-y-6 max-w-2xl animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-surface border border-border-subtle rounded-xs text-[11px] uppercase tracking-[0.2em] font-semibold text-primary">
                <Sparkles className="w-3.5 h-3.5" />
                Edição Autoral Limitada
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-normal text-ink leading-[1.1] tracking-tight">
                {settings.hero_title || 'A Poética da Matéria e do Tempo'}
              </h1>

              <p className="text-base sm:text-lg text-ink-muted leading-relaxed font-light">
                {settings.hero_subtitle ||
                  'Design autoral e criações concebidas para quem busca calma, textura e autenticidade no cotidiano.'}
              </p>

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  onClick={() => onNavigate('/catalogo')}
                  className="bg-ink text-ink-inverted px-8 py-4 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-all flex items-center justify-center gap-2 group shadow-subtle"
                >
                  Explorar o Catálogo
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>

              {/* Factual Highlights */}
              <div className="pt-8 border-t border-border-subtle grid grid-cols-3 gap-6 text-xs text-ink-muted">
                <div>
                  <p className="font-mono font-bold text-ink text-sm">100%</p>
                  <p className="mt-0.5">Design Autoral</p>
                </div>
                <div>
                  <p className="font-mono font-bold text-ink text-sm">Exclusivo</p>
                  <p className="mt-0.5">Produção Limitada</p>
                </div>
                <div>
                  <p className="font-mono font-bold text-ink text-sm">Brasil</p>
                  <p className="mt-0.5">Manufatura Consciente</p>
                </div>
              </div>
            </div>

            {/* Right Hero Image Composition */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-xs overflow-hidden border border-border-subtle shadow-elevated bg-surface">
                <img
                  src={
                    settings.hero_image_url ||
                    'https://images.unsplash.com/photo-1616046229478-9901c5536a45?auto=format&fit=crop&w=1200&q=85'
                  }
                  alt={settings.brand_name || 'Aura Ateliê'}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 bg-surface/90 backdrop-blur-xs p-4 rounded-xs border border-border-subtle text-xs">
                  <p className="font-serif font-medium text-ink">
                    {settings.brand_tagline || 'Design & Ofício Contemporâneo'}
                  </p>
                  <p className="text-ink-muted text-[11px] mt-0.5">
                    {settings.brand_description || 'Criações exclusivas com matérias-primas selecionadas.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Featured Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 pb-4 border-b border-border-subtle">
          <div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
              Curadoria por Material
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-1">
              Coleções & Categorias
            </h2>
          </div>
          <button
            onClick={() => onNavigate('/catalogo')}
            className="mt-4 md:mt-0 text-xs uppercase tracking-wider font-medium text-primary hover:text-primary-hover flex items-center gap-1"
          >
            Ver todos os produtos <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => onNavigate(`/categoria/${cat.slug}`)}
              className="group cursor-pointer bg-surface border border-border-subtle rounded-xs overflow-hidden hover:border-ink/30 transition-all shadow-subtle flex flex-col"
            >
              <div className="aspect-[3/2] overflow-hidden bg-background-alt relative">
                <img
                  src={
                    cat.image_url ||
                    'https://images.unsplash.com/photo-1610701596007-11502861dcfa?auto=format&fit=crop&w=600&q=80'
                  }
                  alt={cat.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-serif text-lg font-medium text-ink group-hover:text-primary transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-ink-muted mt-1 line-clamp-2 leading-relaxed">
                    {cat.description}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between text-xs text-primary font-medium">
                  <span>Descobrir peças</span>
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Featured Products Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 pb-4 border-b border-border-subtle">
          <div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
              Seleção do Ateliê
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-1">
              Destaques do Nosso Acervo
            </h2>
          </div>
          <p className="text-xs text-ink-muted max-w-sm mt-2 md:mt-0">
            Peças selecionadas com tiragens reduzidas e materiais nobres de origem brasileira rastreada.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse space-y-4">
                <div className="aspect-[4/5] bg-surface-muted rounded-xs" />
                <div className="h-4 bg-surface-muted rounded-xs w-2/3" />
                <div className="h-3 bg-surface-muted rounded-xs w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((prod) => (
              <ProductCard key={prod.id} product={prod} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </section>

      {/* 4. Brand Philosophy & Craftsmanship Pillars */}
      <section className="bg-background-alt border-y border-border-subtle py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16 space-y-3">
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-primary">
              Nossos Compromissos
            </span>
            <h2 className="text-3xl font-serif font-normal text-ink">
              Rigor no Ofício e Transparência na Criação
            </h2>
            <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
              Cada objeto que sai do nosso ateliê carrega a história de quem o fez, o respeito pelo tempo dos materiais e a recusa do descarte acelerado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-surface p-8 rounded-xs border border-border-subtle space-y-4">
              <div className="w-10 h-10 rounded-xs bg-primary-subtle text-primary flex items-center justify-center">
                <Flame className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl font-medium text-ink">A Queima & o Grés</h3>
              <p className="text-xs text-ink-muted leading-relaxed">
                Utilizamos argilas de alta temperatura queimadas a 1240°C. Essa fusão vitrifica os minerais, resultando em peças atóxicas, impermeáveis e feitas para durar gerações.
              </p>
            </div>

            <div className="bg-surface p-8 rounded-xs border border-border-subtle space-y-4">
              <div className="w-10 h-10 rounded-xs bg-primary-subtle text-primary flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl font-medium text-ink">Fibras Nobres & Linho Puro</h3>
              <p className="text-xs text-ink-muted leading-relaxed">
                Nossos têxteis são 100% linho vegetal lavado em pedra pomes. O tecido preserva a respirabilidade natural da fibra e ganha maciez tátil a cada uso.
              </p>
            </div>

            <div className="bg-surface p-8 rounded-xs border border-border-subtle space-y-4">
              <div className="w-10 h-10 rounded-xs bg-primary-subtle text-primary flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl font-medium text-ink">Segurança Criptográfica</h3>
              <p className="text-xs text-ink-muted leading-relaxed">
                Seus dados pessoais, CPF e endereços são protegidos com criptografia AES-256-GCM antes de qualquer gravação no banco de dados. Privacidade absoluta e respeito à LGPD.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
